// Take price modifier options for flexibility indicators
export type TakePriceModifier = '+' | '-' | '+/-' | 'Motivated' | 'Firm' | 'Some Room'

export const TAKE_PRICE_MODIFIERS: TakePriceModifier[] = ['+', '-', '+/-', 'Motivated', 'Firm', 'Some Room']

export interface UserProfile {
  id: string
  display_name: string | null
  account_type: string | null
  created_at: string
  updated_at: string
}

export interface Option {
  option_id: string
  model_id: number
  name: string
  value: number
  created_at: string
  updated_at: string
}

export interface MarketAssignment {
  id: string
  user_id: string
  model_id: number
  created_at: string
  updated_at: string
}

export interface Aircraft {
  aircraft_id: number
  options: string[] // Array of option_ids (UUIDs)
  created_at: string
  updated_at: string
  // Off-market functionality
  is_off_market: boolean
  off_market_category?: MarketCategory | null
  off_market_model_id?: number | null
  off_market_created_at?: string | null
  off_market_created_by?: string | null
}

export interface AircraftOverride {
  id: string
  aircraft_id: number
  aftt?: number | null
  tbo?: number | null
  landings?: number | null
  pax?: number | null
  base_airport?: string | null
  base_city?: string | null
  base_state?: string | null
  base_country?: string | null
  for_sale?: boolean | null
  leased?: boolean | null
  asking_price?: number | null
  take_price?: number | null
  take_price_modifier?: TakePriceModifier | null
  external_asking_price?: number | null
  external_take_price?: number | null
  external_take_price_modifier?: TakePriceModifier | null
  actual_ask_make_offer?: boolean | null
  adj_ask_make_offer?: boolean | null
  emp?: string | null
  status?: string | null
  maintenance_program?: string | null
  damage_history?: 'Yes' | 'No' | 'Unknown' | null
  date_listed?: string | null // ISO date string
  date_purchased?: string | null // ISO date string
  year_manufactured?: number | null
  year_delivered?: number | null
  paint_date?: number | null // Year only (e.g. 2024)
  interior_date?: number | null // Year only (e.g. 2024)
  custom_1_value?: string | null
  custom_2_value?: string | null
  custom_3_value?: string | null
  owner?: number | null
  broker?: number | null
  owner_custom?: string | null
  broker_custom?: string | null
  created_at: string
  created_by?: string | null
  updated_at: string
}

export interface AircraftOverrideInput {
  aircraft_id: number
  aftt?: number | null
  tbo?: number | null
  landings?: number | null
  pax?: number | null
  base_airport?: string | null
  base_city?: string | null
  base_state?: string | null
  base_country?: string | null
  for_sale?: boolean | null
  leased?: boolean | null
  asking_price?: number | null
  take_price?: number | null
  take_price_modifier?: TakePriceModifier | null
  external_asking_price?: number | null
  external_take_price?: number | null
  external_take_price_modifier?: TakePriceModifier | null
  actual_ask_make_offer?: boolean | null
  adj_ask_make_offer?: boolean | null
  emp?: string | null
  status?: string | null
  maintenance_program?: string | null
  damage_history?: 'Yes' | 'No' | 'Unknown' | null
  date_listed?: string | null // ISO date string
  date_purchased?: string | null // ISO date string
  year_manufactured?: number | null
  year_delivered?: number | null
  paint_date?: number | null // Year only (e.g. 2024)
  interior_date?: number | null // Year only (e.g. 2024)
  custom_1_value?: string | null
  custom_2_value?: string | null
  custom_3_value?: string | null
  owner?: number | null
  broker?: number | null
  owner_custom?: string | null
  broker_custom?: string | null
}

export interface TransactionOverride {
  override_id: string
  transaction_id: string

  // Pricing overrides (internal and external)
  sale_price?: number | null
  asking_price?: number | null
  take_price?: number | null
  take_price_modifier?: TakePriceModifier | null
  external_sale_price?: number | null
  external_asking_price?: number | null
  external_take_price?: number | null
  external_take_price_modifier?: TakePriceModifier | null
  actual_ask_make_offer?: boolean | null
  adj_ask_make_offer?: boolean | null

  // Technical specification overrides
  aftt?: number | null
  tbo?: number | null
  landings?: number | null
  pax?: number | null
  year_manufactured?: number | null
  year_delivered?: number | null

  // Location overrides
  base_airport?: string | null
  base_city?: string | null
  base_state?: string | null
  base_country?: string | null

  // Status and maintenance overrides
  status?: string | null
  maintenance_program?: string | null
  damage_history?: string | null
  emp?: string | null

  // Market status overrides
  for_sale?: boolean | null
  leased?: boolean | null
  date_listed?: string | null // ISO date string
  date_purchased?: string | null // ISO date string

  // Refurbishment year overrides
  paint_date?: number | null // Year only (e.g. 2024)
  interior_date?: number | null // Year only (e.g. 2024)

  // Company overrides (owner/broker)
  owner?: number | null
  broker?: number | null
  owner_custom?: string | null
  broker_custom?: string | null

  // Custom column overrides
  custom_1_value?: string | null
  custom_2_value?: string | null
  custom_3_value?: string | null

  // Options override (array of option UUIDs)
  options?: string[] | null

  // Audit fields
  created_at: string
  updated_at: string
  created_by?: string | null
  updated_by?: string | null
}

export interface TransactionOverrideInput {
  transaction_id: string

  // Pricing overrides (internal and external)
  sale_price?: number | null
  asking_price?: number | null
  take_price?: number | null
  take_price_modifier?: TakePriceModifier | null
  external_sale_price?: number | null
  external_asking_price?: number | null
  external_take_price?: number | null
  external_take_price_modifier?: TakePriceModifier | null

  // Technical specification overrides
  aftt?: number | null
  tbo?: number | null
  landings?: number | null
  pax?: number | null
  year_manufactured?: number | null
  year_delivered?: number | null

  // Location overrides
  base_airport?: string | null
  base_city?: string | null
  base_state?: string | null
  base_country?: string | null

  // Status and maintenance overrides
  status?: string | null
  maintenance_program?: string | null
  damage_history?: string | null
  emp?: string | null

  // Market status overrides
  for_sale?: boolean | null
  leased?: boolean | null
  date_listed?: string | null // ISO date string
  date_purchased?: string | null // ISO date string

  // Refurbishment year overrides
  paint_date?: number | null // Year only (e.g. 2024)
  interior_date?: number | null // Year only (e.g. 2024)

  // Company overrides (owner/broker)
  owner?: number | null
  broker?: number | null
  owner_custom?: string | null
  broker_custom?: string | null

  // Custom column overrides
  custom_1_value?: string | null
  custom_2_value?: string | null
  custom_3_value?: string | null

  // Options override (array of option UUIDs)
  options?: string[] | null
}

export interface MarketEMP {
  id: string
  model_id: number
  name: string
  description?: string | null
  cost_per_hour?: number | null
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface MarketEMPInput {
  model_id: number
  name: string
  description?: string | null
  cost_per_hour?: number | null
  is_active?: boolean
}

export type MarketCategory = 'for_sale' | 'recently_sold' | 'removed_from_market'

export interface OffMarket {
  id: string
  aircraft_id: number
  model_id: number
  market_category: MarketCategory
  created_at: string
  created_by?: string | null
  updated_at: string
  updated_by?: string | null
}

export interface OffMarketInput {
  aircraft_id: number
  model_id: number
  market_category: MarketCategory
}

// New unified aircraft input for off-market functionality
export interface AircraftOffMarketInput {
  aircraft_id: number
  off_market_category: MarketCategory
  off_market_model_id: number
  options?: string[]
}

export interface AircraftTransaction {
  transaction_id: string
  aircraft_id: number
  model_id: number
  transaction_type: 'sale'
  transaction_date: string
  sale_price?: number | null

  // Reference to original API transaction to prevent duplicates
  api_transaction_id?: number | null

  // Consolidation metadata for back-to-back sales
  source_transaction_ids?: number[] | null  // Array of original API transaction IDs that were consolidated
  is_consolidated?: boolean | null          // Flag indicating this is a consolidated transaction
  consolidation_count?: number | null       // Number of transactions that were consolidated

  // Snapshot of aircraft data at time of sale
  asking_price?: number | null
  take_price?: number | null
  take_price_modifier?: TakePriceModifier | null
  aftt?: number | null
  yeardlv?: number | null
  sernbr?: string | null
  regnbr?: string | null
  make?: string | null
  model?: string | null
  base_airport?: string | null
  base_city?: string | null
  base_state?: string | null
  base_country?: string | null
  damage_history?: string | null
  emp?: string | null
  status?: string | null
  maintenance_program?: string | null

  // Enhanced override fields for comprehensive snapshots
  external_asking_price?: number | null
  external_take_price?: number | null
  external_take_price_modifier?: TakePriceModifier | null
  external_sale_price?: number | null
  tbo?: number | null
  landings?: number | null
  pax?: number | null
  owner?: number | null
  broker?: number | null
  owner_custom?: string | null
  broker_custom?: string | null
  for_sale?: boolean | null
  leased?: boolean | null
  date_listed?: string | null // ISO date string
  date_purchased?: string | null // ISO date string
  paint_date?: number | null // Year only (e.g. 2024)
  interior_date?: number | null // Year only (e.g. 2024)
  custom_1_value?: string | null
  custom_2_value?: string | null
  custom_3_value?: string | null
  options?: string[] | null // Array of option_ids (UUIDs)
  actual_ask_make_offer?: boolean | null
  adj_ask_make_offer?: boolean | null

  // Audit fields
  created_at: string
  updated_at: string
  created_by?: string | null
  updated_by?: string | null

  // Transaction-specific notes
  transaction_notes?: string | null
}

export interface AircraftTransactionInput {
  aircraft_id: number
  model_id: number
  transaction_date: string
  sale_price?: number | null
  api_transaction_id?: number | null

  // Consolidation metadata for back-to-back sales
  source_transaction_ids?: number[] | null
  is_consolidated?: boolean | null
  consolidation_count?: number | null

  // Optional snapshot data (will be auto-populated if not provided)
  asking_price?: number | null
  take_price?: number | null
  take_price_modifier?: TakePriceModifier | null
  aftt?: number | null
  yeardlv?: number | null
  sernbr?: string | null
  regnbr?: string | null
  make?: string | null
  model?: string | null
  base_airport?: string | null
  base_city?: string | null
  base_state?: string | null
  base_country?: string | null
  damage_history?: string | null
  emp?: string | null
  status?: string | null
  maintenance_program?: string | null

  // Enhanced override fields for comprehensive snapshots
  external_asking_price?: number | null
  external_take_price?: number | null
  external_take_price_modifier?: TakePriceModifier | null
  external_sale_price?: number | null
  tbo?: number | null
  landings?: number | null
  pax?: number | null
  owner?: number | null
  broker?: number | null
  owner_custom?: string | null
  broker_custom?: string | null
  for_sale?: boolean | null
  leased?: boolean | null
  date_listed?: string | null
  date_purchased?: string | null
  paint_date?: number | null // Year only (e.g. 2024)
  interior_date?: number | null // Year only (e.g. 2024)
  custom_1_value?: string | null
  custom_2_value?: string | null
  custom_3_value?: string | null
  options?: string[] | null
  actual_ask_make_offer?: boolean | null
  adj_ask_make_offer?: boolean | null

  // Transaction-specific notes
  transaction_notes?: string | null
}

// Aircraft Movement System Types
export type ContainerType = 'for_sale' | 'sold' | 'removed_from_market'
export type MovementStatus = 'pending' | 'confirmed' | 'undone' | 'expired'

export interface AircraftMovement {
  movement_id: string
  aircraft_id: number
  model_id: number

  // Movement details
  from_container: ContainerType
  to_container: ContainerType
  movement_reason?: string | null

  // Status and timing
  status: MovementStatus
  moved_at: string
  expires_at: string
  confirmed_at?: string | null
  undone_at?: string | null

  // User tracking
  moved_by?: string | null
  undone_by?: string | null

  // Snapshot references
  created_snapshot_id?: string | null
  deleted_snapshot_id?: string | null

  // Metadata
  notes?: string | null
  created_at: string
  updated_at: string
}

export interface AircraftMovementInput {
  aircraft_id: number
  model_id: number
  from_container: ContainerType
  to_container: ContainerType
  movement_reason?: string | null
  moved_by?: string | null
  notes?: string | null
}

export interface TemporaryAircraftState {
  state_id: string
  movement_id: string
  aircraft_id: number
  model_id: number
  container: ContainerType

  // Data snapshots
  aircraft_data: any // Full aircraft object
  override_data?: any | null // Override data

  // Display and status
  display_order: number
  is_active: boolean

  created_at: string
  updated_at: string
}

export interface MovementValidationRule {
  rule_id: string
  rule_name: string
  from_container?: ContainerType | null
  to_container?: ContainerType | null

  // Rule configuration
  requires_api_presence: boolean
  requires_off_market_flag: boolean
  max_age_days?: number | null
  min_time_in_container_hours?: number | null

  // Metadata
  description?: string | null
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface UndoableMovement extends AircraftMovement {
  can_undo: boolean
  hours_until_expiry: number
}

// Combined aircraft data that includes both API and off-market info
export interface UnifiedAircraftData {
  // Core aircraft data (from IntelliJetNet API)
  aircraft_id: number
  model_id: number | null
  sernbr: string | null
  regnbr: string | null
  yearmfr: number | null
  make: string | null
  model: string | null
  forsale: boolean | null
  is_active: boolean
  // ... other API fields

  // Local Supabase data
  options: string[]
  is_off_market: boolean
  off_market_category?: MarketCategory | null
  off_market_model_id?: number | null
  off_market_created_at?: string | null
  off_market_created_by?: string | null

  // Metadata
  data_source: 'api' | 'off_market' | 'hybrid'
  has_conflict?: boolean
}

export interface AircraftNote {
  id: string
  aircraft_id: number
  content: string
  created_at: string
  created_by: string
  updated_at: string
  updated_by?: string | null
}

export interface AircraftNoteInput {
  aircraft_id: number
  content: string
  created_at?: string // Optional custom timestamp, defaults to now
}

export interface AircraftNoteUpdate {
  content: string
  created_at?: string // Optional timestamp override
}

export interface MarketCustomColumn {
  id: string
  model_id: number
  column_index: 1 | 2 | 3
  column_title: string
  input_type: 'text' | 'dropdown'
  options?: string[] | null
  created_at: string
  updated_at: string
}

export interface MarketCustomColumnInput {
  model_id: number
  column_index: 1 | 2 | 3
  column_title: string
  input_type: 'text' | 'dropdown'
  options?: string[] | null
}

// Aircraft Acknowledgments System Types
export type ChangeType = 'price_change' | 'new_to_market'

export interface AircraftAcknowledgment {
  id: string
  aircraft_id: number
  acknowledged_by: string
  acknowledged_at: string
  change_type: ChangeType
  previous_value?: Record<string, any> | null
  new_value?: Record<string, any> | null
  created_at: string
}

export interface AircraftAcknowledgmentInput {
  aircraft_id: number
  change_type: ChangeType
  previous_value?: Record<string, any> | null
  new_value?: Record<string, any> | null
}

export interface AircraftChangeInfo {
  aircraft_id: number
  has_price_change: boolean
  is_new_to_market: boolean
  is_under_contract: boolean
  price_change_details?: {
    previous_asking?: string | number | null
    new_asking?: string | number | null
    previous_take?: number | null
    new_take?: number | null
  }
  new_listing_date?: string | null
  last_acknowledged?: {
    price_change?: string | null
    new_to_market?: string | null
  }
}

export interface MarketCustomColumnUpdate {
  column_title?: string
  input_type?: 'text' | 'dropdown'
  options?: string[] | null
}

export interface Database {
  public: {
    Tables: {
      profiles: {
        Row: UserProfile
        Insert: Omit<UserProfile, 'created_at' | 'updated_at'> & {
          created_at?: string
          updated_at?: string
        }
        Update: Partial<Omit<UserProfile, 'id' | 'created_at' | 'updated_at'>>
      }
      options: {
        Row: Option
        Insert: Omit<Option, 'option_id' | 'created_at' | 'updated_at'> & {
          option_id?: string
          created_at?: string
          updated_at?: string
        }
        Update: Partial<Omit<Option, 'option_id' | 'created_at' | 'updated_at'>>
      }
      market_assignments: {
        Row: MarketAssignment
        Insert: Omit<MarketAssignment, 'id' | 'created_at' | 'updated_at'> & {
          id?: string
          created_at?: string
          updated_at?: string
        }
        Update: Partial<Omit<MarketAssignment, 'id' | 'created_at' | 'updated_at'>>
      }
      aircraft: {
        Row: Aircraft
        Insert: Omit<Aircraft, 'created_at' | 'updated_at'> & {
          created_at?: string
          updated_at?: string
        }
        Update: Partial<Omit<Aircraft, 'aircraft_id' | 'created_at' | 'updated_at'>>
      }
      off_market: {
        Row: OffMarket
        Insert: Omit<OffMarket, 'id' | 'created_at' | 'updated_at' | 'created_by' | 'updated_by'> & {
          id?: string
          created_at?: string
          updated_at?: string
          created_by?: string | null
          updated_by?: string | null
        }
        Update: Partial<Omit<OffMarket, 'id' | 'created_at' | 'created_by'>>
      }
      aircraft_notes: {
        Row: AircraftNote
        Insert: Omit<AircraftNote, 'id' | 'created_at' | 'updated_at' | 'updated_by'> & {
          id?: string
          created_at?: string
          updated_at?: string
          updated_by?: string | null
        }
        Update: Partial<Omit<AircraftNote, 'id' | 'created_by'>> & {
          created_at?: string
        }
      }
      aircraft_transactions: {
        Row: AircraftTransaction
        Insert: Omit<AircraftTransaction, 'transaction_id' | 'created_at' | 'updated_at' | 'updated_by'> & {
          transaction_id?: string
          created_at?: string
          updated_at?: string
          updated_by?: string | null
        }
        Update: Partial<Omit<AircraftTransaction, 'transaction_id' | 'created_at' | 'created_by'>>
      }
      user_hidden_columns: {
        Row: UserHiddenColumns
        Insert: Omit<UserHiddenColumns, 'id' | 'created_at' | 'updated_at'> & {
          id?: string
          created_at?: string
          updated_at?: string
        }
        Update: Partial<Omit<UserHiddenColumns, 'id' | 'created_at'>>
      }
    }
  }
}

export interface UserHiddenColumns {
  id: string
  user_id: string
  model_id: number
  hidden_columns: string[]
  created_at: string
  updated_at: string
}

export interface HiddenTransaction {
  id: string
  model_id: number
  transaction_identifier: string // aircraftTransaction.transaction_id (UUID) or 'api-{trans_id}' for API-only
  hidden_by?: string | null
  created_at: string
}